import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  RadioGroup, Radio, FormControlLabel, CircularProgress, Chip, Alert
} from '@mui/material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

// rows: [{ materialName, quantity, unit, amount, gstPercentage, matchedItem: item|null, rowIndex }]
// matchedItem is pre-filled for exact/prefix matches from the caller.
// onConfirm(resolvedRows) — each resolvedRow has either materialItemId+expenseCategoryId set, or stays free text.
const ExcelImportReview = ({ open, rows, departmentId, onConfirm, onCancel }) => {
  const [reviewRows, setReviewRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);

    const fetchSuggestions = async () => {
      const token = localStorage.getItem('token');
      const CONCURRENCY = 4;
      const results = new Array(rows.length);

      const runBatch = async (startIdx) => {
        const row = rows[startIdx];
        if (row.matchedItem) {
          results[startIdx] = { ...row, choice: `existing_${row.matchedItem.id}`, suggestions: [] };
          return;
        }
        try {
          const res = await axios.get(`${API_BASE_URL}/material-items/suggest`, {
            params: { q: row.materialName, department_id: departmentId },
            headers: { Authorization: `Bearer ${token}` }
          });
          const suggestions = res.data.data || [];
          results[startIdx] = {
            ...row,
            suggestions,
            choice: suggestions.length > 0 ? `existing_${suggestions[0].id}` : 'new'
          };
        } catch (e) {
          results[startIdx] = { ...row, suggestions: [], choice: 'new' };
        }
      };

      let cursor = 0;
      const workers = Array.from({ length: CONCURRENCY }).map(async () => {
        while (cursor < rows.length) {
          const idx = cursor++;
          await runBatch(idx);
        }
      });
      await Promise.all(workers);

      setReviewRows(results);
      setLoading(false);
    };

    fetchSuggestions();
  }, [open, rows, departmentId]);

  const handleChoiceChange = (idx, value) => {
    setReviewRows(prev => prev.map((r, i) => i === idx ? { ...r, choice: value } : r));
  };

  const handleConfirm = () => {
    const resolved = reviewRows.map(r => {
      if (r.choice === 'new') {
        return { ...r, materialItemId: null, useExisting: null };
      }
      const itemId = parseInt(r.choice.replace('existing_', ''), 10);
      const item = r.matchedItem?.id === itemId
        ? r.matchedItem
        : r.suggestions.find(s => s.id === itemId);
      return { ...r, materialItemId: item.id, useExisting: item };
    });
    onConfirm(resolved);
  };

  const unmatchedNeedingReview = reviewRows.filter(r => !r.matchedItem && r.suggestions?.length > 0).length;

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>Review Imported Items</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Checking for existing materials...</Typography>
          </Box>
        ) : (
          <>
            {unmatchedNeedingReview > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {unmatchedNeedingReview} item(s) look similar to materials already in the catalog — review before adding.
              </Alert>
            )}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Typed Name</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Decision</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reviewRows.map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{r.materialName}</TableCell>
                      <TableCell>{r.quantity} {r.unit}</TableCell>
                      <TableCell>
                        {r.matchedItem ? (
                          <Chip size="small" color="success" label={`Auto-linked: ${r.matchedItem.itemName}`} />
                        ) : (
                          <RadioGroup value={r.choice} onChange={(e) => handleChoiceChange(idx, e.target.value)}>
                            {r.suggestions.map(s => (
                              <FormControlLabel
                                key={s.id}
                                value={`existing_${s.id}`}
                                control={<Radio size="small" />}
                                label={`Use existing: ${s.itemName}`}
                              />
                            ))}
                            <FormControlLabel
                              value="new"
                              control={<Radio size="small" />}
                              label={`Add "${r.materialName}" as new material`}
                            />
                          </RadioGroup>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading}>
          Confirm & Add {reviewRows.length} Item(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExcelImportReview;